(() => {
  if (window.__thbHubV2Loaded) return;
  window.__thbHubV2Loaded = true;
  const instances = new WeakMap();

  class Hub {
    constructor(root) {
      this.root = root;
      this.abort = new AbortController();
      this.observers = [];
      this.frames = new Set();
      root.querySelectorAll('[data-hub-carousel]').forEach(container => this.carousel(container));
      root.addEventListener('shopify:block:select', event => {
        const block = event.target.closest('[data-hub-track] > *');
        if (block) this.reveal(block);
      }, {signal: this.abort.signal});
    }
    reveal(card) {
      const track = card.parentElement;
      const outer = track.getBoundingClientRect();
      const box = card.getBoundingClientRect();
      const delta = box.left < outer.left ? box.left - outer.left : box.right > outer.right ? box.right - outer.right : 0;
      if (Math.abs(delta) > 1) track.scrollBy({left: delta, behavior: 'instant'});
    }
    carousel(container) {
      const track = container.querySelector('[data-hub-track]');
      const controls = container.querySelector('[data-hub-controls]');
      if (!track || !controls) return;
      const previous = controls.querySelector('[data-hub-prev]');
      const next = controls.querySelector('[data-hub-next]');
      const dots = controls.querySelector('[data-hub-dots]');
      const status = controls.querySelector('[data-hub-status]');
      const signal = this.abort.signal;
      let starts = [0];
      let queued = false;
      const position = () => Math.abs(track.scrollLeft);
      const update = () => {
        queued = false;
        const items = [...track.children];
        const max = Math.max(0, track.scrollWidth - track.clientWidth);
        const rtl = getComputedStyle(track).direction === 'rtl';
        const bounds = track.getBoundingClientRect();
        const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
        const width = items[0]?.getBoundingClientRect().width || track.clientWidth;
        const perPage = Math.max(1, Math.floor((track.clientWidth + gap + 1) / (width + gap)));
        starts = [0];
        items.forEach((item, index) => {
          const box = item.getBoundingClientRect();
          const offset = Math.round((rtl ? bounds.right - box.right : box.left - bounds.left) + position());
          if (index > 0 && index % perPage === 0 && offset > 1 && offset < max) starts.push(offset);
        });
        if (max > 2 && Math.abs(starts.at(-1) - max) > 2) starts.push(max);
        const current = starts.reduce((best, value, index) => Math.abs(value - position()) < Math.abs(starts[best] - position()) ? index : best, 0);
        controls.hidden = max <= 2;
        previous.disabled = position() <= 2;
        next.disabled = position() >= max - 2;
        previous.setAttribute('aria-disabled', String(previous.disabled));
        next.setAttribute('aria-disabled', String(next.disabled));
        if (dots.children.length !== starts.length) dots.replaceChildren(...starts.map(() => document.createElement('i')));
        [...dots.children].forEach((dot, index) => dot.setAttribute('aria-current', String(index === current)));
        if (status) status.textContent = (status.dataset.format || '').replace('__index__', String(current + 1)).replace('__length__', String(starts.length));
      };
      const schedule = () => {
        if (queued) return;
        queued = true;
        const id = requestAnimationFrame(() => {this.frames.delete(id); update();});
        this.frames.add(id);
      };
      const go = direction => {
        update();
        let index;
        if (direction > 0) index = starts.findIndex(start => start > position() + 2);
        else {
          index = starts.length - 1;
          while (index > 0 && starts[index] >= position() - 2) index--;
        }
        if (index < 0) index = starts.length - 1;
        const sign = getComputedStyle(track).direction === 'rtl' ? -1 : 1;
        track.scrollTo({left: starts[index] * sign, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'});
      };
      previous.addEventListener('click', () => go(-1), {signal});
      next.addEventListener('click', () => go(1), {signal});
      track.addEventListener('scroll', schedule, {signal, passive: true});
      track.addEventListener('focusin', event => {
        const card = [...track.children].find(item => item.contains(event.target));
        if (card) this.reveal(card);
        schedule();
      }, {signal});
      if ('ResizeObserver' in window) {
        const observer = new ResizeObserver(schedule);
        observer.observe(track);
        this.observers.push(observer);
      } else window.addEventListener('resize', schedule, {signal});
      update();
    }
    destroy() {
      this.abort.abort();
      this.observers.forEach(observer => observer.disconnect());
      this.frames.forEach(id => cancelAnimationFrame(id));
    }
  }

  const rootsIn = scope => [
    ...(scope.matches?.('[data-thb-hub]') ? [scope] : []),
    ...scope.querySelectorAll('[data-thb-hub]')
  ];
  const init = scope => rootsIn(scope).forEach(root => {
    if (!instances.has(root)) instances.set(root, new Hub(root));
  });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(document), {once: true});
  else init(document);
  document.addEventListener('shopify:section:load', event => init(event.target));
  document.addEventListener('shopify:section:unload', event => rootsIn(event.target).forEach(root => {
    instances.get(root)?.destroy();
    instances.delete(root);
  }));
})();
